import { html, cx, useState, useRef, useEffect } from '../h.js'
import { Icon } from '../icons.js'
import { t } from '../i18n.js'
import { copyText } from '../format.js'
import { useToast } from '../components.js'
import { useApp } from '../store.js'
import { QrCode } from '../qr.js'
import { LinkOffer, normalizeUsername, usernameError, nameError } from '@pine/core'
import { canRememberKey, rememberKey, recallKey } from '../keysave.js'

/**
 * First run.
 *
 * There is no sign-up because there is nothing to sign up to: a profile is a
 * keypair generated on the device. The one thing that genuinely cannot be
 * recovered is the seed, so the flow spends a whole step making sure the user
 * has written it down before letting them past.
 */
export function Onboarding ({ client, onDone }) {
  const store = useApp()
  const [step, setStep] = useState('welcome')
  const [name, setName] = useState('')
  const [handle, setHandle] = useState('')
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [linkCode, setLinkCode] = useState('')
  const toast = useToast()
  const inputRef = useRef(null)
  const offerRef = useRef(null)

  const desktop = store && store.platform && store.platform.kind === 'desktop'

  /*
   * The last step of setting up, on the platforms that have one.
   *
   * Android will not deliver a call or a message to an app it has put to
   * sleep, and the permissions that stop it doing that are not ours to grant —
   * they are three separate system screens, and an app that waits for somebody
   * to go looking for them in Settings is an app whose calls do not arrive.
   * Reported exactly that way, twice.
   *
   * So it is part of first run, not a tip. Desktop and the browser have no
   * such thing and never see this step.
   */
  const access = store && store.platform && store.platform.background
  const needsAccess = !!(access && access.available)

  /** Onboarding is over — unless this phone still has to be asked. */
  function finish () {
    if (needsAccess) setStep('access')
    else onDone()
  }

  useEffect(() => {
    if ((step === 'name' || step === 'restore' || step === 'username') && inputRef.current) {
      inputRef.current.focus()
    }
  }, [step])

  // A pending offer holds a DHT topic open. Leaving the screen closes it.
  useEffect(() => () => {
    if (offerRef.current) offerRef.current.cancel()
  }, [])

  /**
   * Sign in by being handed the account from a device that already has it.
   *
   * This device has no account, so it makes a throwaway keypair, shows it, and
   * waits. Everything about how that is kept safe is in lib/linking.js.
   */
  async function startLinking () {
    setError('')
    setStep('link')

    if (offerRef.current) offerRef.current.cancel()
    const offer = new LinkOffer(client.transport)
    offerRef.current = offer
    setLinkCode(offer.code)

    const result = await offer.start()
    offerRef.current = null
    if (!result) {
      setLinkCode('')
      setError(t('link.expired'))
      setStep('welcome')
      return
    }

    // adoptSeed repoints everything that holds the account key and waits for
    // the chat list, the profile and the handle to come back with it.
    await client.adoptSeed(result.seed, result.profile)
    onDone()
  }

  async function createProfile () {
    const problem = nameError(name)
    if (problem) return setError(t(problem === 'emoji' ? 'name.err.emoji' : 'err.needName'))
    setBusy(true)
    try {
      await client.updateProfile({ name: name.trim() })
      setKey(client.identity.recoveryKey)
      setStep('username')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Offer a handle on the way in.
   *
   * Without one you are findable only by a 52-character key or by a link, and
   * somebody who has just installed the app has neither to give out. It is
   * offered rather than demanded — skipping is one tap, and Settings has the
   * same field — because a name that is already taken must never be a wall
   * between a person and the app they just installed.
   */
  async function claimHandle () {
    const normalized = normalizeUsername(handle)
    const problem = usernameError(normalized)
    if (problem) return setError(t('username.err.' + problem))

    setBusy(true)
    setError('')
    try {
      await client.setUsername(normalized)
      setStep('key')
    } catch (err) {
      const reason = err.reason || 'failed'
      // Offline is not a refusal: the handle is kept and announced on the DHT,
      // and the registry gets it on the next launch. Nothing is gained by
      // holding up the first run for it.
      if (reason === 'offline') setStep('key')
      else setError(t('username.err.' + reason))
    } finally {
      setBusy(false)
    }
  }

  const keyInBrowser = canRememberKey(store && store.platform)

  /** The saved key, straight from the browser's password manager. */
  async function restoreFromBrowser () {
    const saved = await recallKey()
    if (!saved) return
    setKey(saved)
    await restoreProfile(saved)
  }

  async function restoreProfile (given) {
    const recovery = typeof given === 'string' ? given : key
    setBusy(true)
    setError('')
    try {
      // Returns once the account's chats, profile and handle are back, so the
      // list behind this screen is already populated when it closes.
      await client.restoreIdentity(recovery.trim())
      // Only a name actually typed here is an edit. Re-saving the one that
      // just came back would stamp it as new and make this device's copy
      // outrank the account's real, newer profile everywhere else.
      const typed = name.trim()
      if (typed && typed !== client.identity.profile.name) {
        await client.updateProfile({ name: typed })
      }
      finish()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const mark = html`<div class="onboard-mark">${Icon.pine(38)}</div>`

  if (step === 'welcome') {
    return html`
      <div class="onboard">
        <div class="onboard-card">
          ${mark}
          <h1>${t('onboard.welcome')}</h1>
          <p class="lede">${t('onboard.lede')}</p>
          <div class="col gap2" style="margin-top:8px">
            <button class="btn btn-primary btn-lg btn-block" onClick=${() => setStep('name')}>
              ${t('onboard.create')}
            </button>
            <button class="btn btn-soft btn-block" onClick=${startLinking}>
              ${Icon.qr(17)} ${t('link.addDevice')}
            </button>
            ${keyInBrowser && html`
              <button class="btn btn-soft btn-block" disabled=${busy} onClick=${restoreFromBrowser}>
                ${Icon.key(17)} ${t('keysave.signIn')}
              </button>`}
            ${error && html`<span class="error">${error}</span>`}
            <button class=${cx('btn btn-block', desktop ? 'btn-ghost btn-sm' : 'btn-ghost')}
                    onClick=${() => setStep('restore')}>
              ${t('onboard.restore')}
            </button>
          </div>
        </div>
      </div>`
  }

  if (step === 'link') {
    return html`
      <div class="onboard">
        <div class="onboard-card">
          <h1 style="font-size:var(--text-2xl)">${t('link.title')}</h1>
          <p class="lede" style="font-size:var(--text-md)">${t('link.lede')}</p>

          ${linkCode
            ? html`
              <div class="qr-frame"><${QrCode} text=${linkCode} /></div>
              <p class="small dim">${t('link.orCode')}</p>
              <div class="recovery-key selectable" style="font-size:11px">${linkCode}</div>
              <button class="btn btn-soft btn-block" onClick=${async () => {
                const ok = await copyText(linkCode)
                toast(ok ? t('action.copied') : t('err.generic'), { error: !ok })
              }}>${Icon.copy(17)} ${t('action.copy')}</button>
              <div class="row center gap2 dim small" style="margin-top:4px">
                <div class="spinner" style="width:13px;height:13px"></div>${t('link.waiting')}
              </div>`
            : html`<div class="empty"><div class="spinner lg"></div></div>`}

          <button class="btn btn-ghost btn-block" onClick=${() => {
            if (offerRef.current) offerRef.current.cancel()
            offerRef.current = null
            setLinkCode('')
            setStep('welcome')
          }}>${t('onboard.back')}</button>
        </div>
      </div>`
  }

  if (step === 'name') {
    return html`
      <div class="onboard">
        <div class="onboard-card">
          ${mark}
          <h1>${t('onboard.name')}</h1>
          <div class="field" style="text-align:left">
            <input ref=${inputRef} class=${cx('input', nameError(name) === 'emoji' && 'invalid')}
                   value=${name} maxlength="64"
                   placeholder=${t('onboard.namePlaceholder')}
                   onInput=${(e) => { setName(e.target.value); setError('') }}
                   onKeyDown=${(e) => { if (e.key === 'Enter') createProfile() }} />
            ${/*
               * Say it while they are typing, not when they press the button:
               * the button is disabled by the same rule, so waiting for a
               * submit would leave a red box and no reason for it.
               */
             nameError(name) === 'emoji'
              ? html`<span class="error">${t('name.err.emoji')}</span>`
              : error && html`<span class="error">${error}</span>`}
          </div>
          <button class="btn btn-primary btn-lg btn-block" disabled=${busy || !!nameError(name)}
                  onClick=${createProfile}>
            ${busy ? html`<div class="spinner"></div>` : t('onboard.continue')}
          </button>
          <button class="btn btn-ghost btn-block" onClick=${() => setStep('welcome')}>${t('onboard.back')}</button>
        </div>
      </div>`
  }

  if (step === 'username') {
    const normalized = normalizeUsername(handle)
    const problem = normalized ? usernameError(normalized) : null

    return html`
      <div class="onboard">
        <div class="onboard-card">
          ${mark}
          <h1 style="font-size:var(--text-2xl)">${t('onboard.username')}</h1>
          <p class="lede" style="font-size:var(--text-md)">${t('onboard.usernameLede')}</p>
          <div class="field" style="text-align:left">
            <div class="input-prefix">
              <span>@</span>
              <input ref=${inputRef} value=${handle} maxlength="32" spellcheck="false"
                     autocapitalize="off" autocomplete="off"
                     placeholder=${t('settings.usernamePlaceholder')}
                     onInput=${(e) => { setHandle(e.target.value); setError('') }}
                     onKeyDown=${(e) => { if (e.key === 'Enter' && normalized && !problem) claimHandle() }} />
            </div>
            ${error
              ? html`<span class="error">${error}</span>`
              : html`<span class="hint">${problem ? t('username.err.' + problem) : t('settings.usernameHint')}</span>`}
          </div>
          <button class="btn btn-primary btn-lg btn-block" disabled=${busy || !normalized || !!problem}
                  onClick=${claimHandle}>
            ${busy ? html`<div class="spinner"></div>` : t('onboard.continue')}
          </button>
          <button class="btn btn-ghost btn-block" disabled=${busy}
                  onClick=${() => { setError(''); setStep('key') }}>
            ${t('onboard.usernameSkip')}
          </button>
        </div>
      </div>`
  }

  if (step === 'access') {
    return html`<${AccessStep} access=${access} onDone=${onDone} />`
  }

  if (step === 'key') {
    return html`
      <div class="onboard">
        <div class="onboard-card">
          <div class="onboard-mark" style="background:var(--amber-soft);color:var(--warning)">${Icon.key(34)}</div>
          <h1 style="font-size:var(--text-2xl)">${t('onboard.keyTitle')}</h1>
          <p class="lede" style="font-size:var(--text-md)">${t('onboard.keyLede')}</p>
          <div class="recovery-key selectable">${key}</div>
          <div class="col gap2">
            <button class="btn btn-soft btn-block" onClick=${async () => {
              const ok = await copyText(key)
              toast(ok ? t('onboard.keyCopied') : t('err.generic'), { error: !ok })
            }}>${Icon.copy(17)} ${t('action.copy')}</button>
            ${keyInBrowser && html`
              <button class="btn btn-soft btn-block" onClick=${async () => {
                try {
                  await rememberKey({ key, name: client.identity.profile.name, account: client.identity.id })
                } catch {
                  toast(t('err.generic'), { error: true })
                }
              }}>${Icon.key(17)} ${t('keysave.save')}</button>`}
            <button class="btn btn-primary btn-lg btn-block" onClick=${finish}>${t('onboard.keySaved')}</button>
          </div>
        </div>
      </div>`
  }

  return html`
    <div class="onboard">
      <div class="onboard-card">
        ${mark}
        <h1 style="font-size:var(--text-2xl)">${t('onboard.restoreTitle')}</h1>
        <p class="lede" style="font-size:var(--text-md)">${t('onboard.restoreLede')}</p>
        <div class="field" style="text-align:left">
          <textarea ref=${inputRef} class="textarea mono" rows="3" value=${key}
                    style="min-height:78px"
                    placeholder="XXXX-XXXX-XXXX-…"
                    onInput=${(e) => { setKey(e.target.value); setError('') }}></textarea>
          <input class="input" value=${name} placeholder=${t('onboard.namePlaceholder')}
                 onInput=${(e) => setName(e.target.value)} />
          ${error && html`<span class="error">${error}</span>`}
        </div>
        ${keyInBrowser && html`
          <button class="btn btn-soft btn-block" disabled=${busy} onClick=${restoreFromBrowser}>
            ${Icon.key(17)} ${t('keysave.fill')}
          </button>`}
        <button class=${cx('btn btn-primary btn-lg btn-block')} disabled=${busy || key.trim().length < 8}
                onClick=${restoreProfile}>
          ${busy ? html`<div class="spinner"></div>` : t('onboard.restoreAction')}
        </button>
        <button class="btn btn-ghost btn-block" onClick=${() => setStep('welcome')}>${t('onboard.back')}</button>
      </div>
    </div>`
}

/**
 * The permissions Android needs before it will let a call through.
 *
 * Three system screens, none of which this app can answer for the person —
 * all it can do is ask, show what is still missing, and refuse to call the
 * setup finished while notifications are off, since without those nothing
 * arrives at all when the app is closed.
 *
 * The other two are pushed hard and not made into a wall. Battery optimisation
 * and full-screen calls live in screens that some manufacturers move, rename,
 * or do not have; a person whose phone has no such screen must not be locked
 * out of the messenger by it. They get an explicit warning instead, and the
 * same three switches wait for them in Settings.
 *
 * State is polled rather than watched. A permission is granted in a system
 * dialog on top of this window, and coming back from one does not reliably
 * fire anything a web page can hear.
 */
function AccessStep ({ access, onDone }) {
  const [status, setStatus] = useState(() => access.status() || {})

  useEffect(() => {
    const read = () => setStatus(access.status() || {})
    const timer = setInterval(read, 1500)
    window.addEventListener('focus', read)
    document.addEventListener('visibilitychange', read)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', read)
      document.removeEventListener('visibilitychange', read)
    }
  }, [access])

  const rows = [
    ['notifications', 'notifications'],
    ['battery', 'battery'],
    ['fullScreen', 'fullscreen']
  ]

  const ready = !!status.notifications
  const complete = ready && !!status.battery && !!status.fullScreen

  return html`
    <div class="onboard">
      <div class="onboard-card">
        <div class="onboard-mark" style="background:var(--amber-soft);color:var(--warning)">
          ${Icon.shield(34)}
        </div>
        <h1 style="font-size:var(--text-2xl)">${t('access.title')}</h1>
        <p class="lede" style="font-size:var(--text-md)">${t('access.lede')}</p>

        <div class="col gap2" style="width:100%;text-align:left">
          ${rows.map(([key, ask]) => html`
            <div key=${key} class="access-row">
              <span class=${cx('access-mark', status[key] && 'on')}>
                ${status[key] ? Icon.check(16) : Icon.shield(16)}
              </span>
              <span class="grow">
                <span class="strong">${t('access.' + key)}</span>
                <span class="small dim">${t('access.' + key + 'Why')}</span>
              </span>
              ${status[key]
                ? html`<span class="small" style="color:var(--accent)">${t('settings.bgAllowed')}</span>`
                : html`
                  <button class="btn btn-sm btn-primary" onClick=${() => access.request(ask)}>
                    ${t('access.allow')}
                  </button>`}
            </div>`)}
        </div>

        ${!ready && html`<p class="hint mute small">${t('access.needNotifications')}</p>`}
        ${ready && !complete && html`<p class="error">${t('access.partial')}</p>`}

        <button class=${cx('btn btn-lg btn-block', complete ? 'btn-primary' : 'btn-soft')}
                disabled=${!ready} onClick=${onDone}>
          ${complete ? t('access.done') : t('access.anyway')}
        </button>
      </div>
    </div>`
}
